import React, { Suspense, useState } from "react"
import Box from "@mui/material/Box"
import CircularProgress from "@mui/material/CircularProgress"

// ── Segmented-toggle workspace ───────────────────────────────────────────────
// Renders a thin segmented control on top and the active sub-page below. The
// sub-page is mounted on demand (inactive tabs unmount); state that must survive
// a switch already lives in global contexts (chat, research). The height
// override tames the sub-pages' `height: 100vh` roots, mirroring FloatingWindow.

const MONO = "var(--app-font-mono)"

export default function TabbedWorkspace({ tabs, initialTabId, color }) {
  const [active, setActive] = useState(() =>
    tabs.some((t) => t.id === initialTabId) ? initialTabId : tabs[0].id
  )
  const tab = tabs.find((t) => t.id === active) ?? tabs[0]
  const Comp = tab.component

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", minHeight: 0 }}>
      {/* Segmented toggle */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.75,
        backgroundColor: "var(--app-bg-alt)", borderBottom: "1px solid rgba(var(--app-fg-rgb),0.06)",
        flexShrink: 0,
      }}>
        {tabs.map((t) => {
          const on = t.id === active
          return (
            <Box
              key={t.id}
              onClick={() => setActive(t.id)}
              sx={{
                px: 1.5, py: 0.5, borderRadius: 1, cursor: "pointer",
                fontFamily: MONO, fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em",
                color: on ? "#000" : "rgba(var(--app-fg-rgb),0.5)",
                backgroundColor: on ? t.color : "transparent",
                border: `1px solid ${on ? t.color : "rgba(var(--app-fg-rgb),0.08)"}`,
                boxShadow: on ? `0 0 10px color-mix(in srgb, ${t.color} 26.7%, transparent)` : "none",
                transition: "background-color 0.15s, color 0.15s, border-color 0.15s",
                "&:hover": { color: on ? "#000" : t.color, borderColor: `color-mix(in srgb, ${t.color} 40%, transparent)` },
              }}
            >
              {t.label}
            </Box>
          )
        })}
      </Box>

      {/* Active sub-page */}
      <Box sx={{
        flex: 1, minHeight: 0, overflow: "hidden", position: "relative",
        "& > *": { height: "100% !important", minHeight: "0 !important" },
      }}>
        <Suspense fallback={
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={22} sx={{ color: color ?? tab.color }} />
          </Box>
        }>
          <Comp />
        </Suspense>
      </Box>
    </Box>
  )
}
